/** MI DOMINO - sound effects (Web Audio). Extracted from the legacy client. */
declare global {
  interface Window { _actx?: AudioContext; webkitAudioContext?: typeof AudioContext; }
}

let MUTED = false;
try { MUTED = localStorage.getItem('mdsound') === '0'; } catch (e) { /* no storage */ }

/** Master sound toggle (persisted). Every SFX flows through ac(), so muting
 *  here silences all of them at one point — no per-sound edits, no backlog. */
export function setSound(on: boolean): void {
  MUTED = !on;
  try { localStorage.setItem('mdsound', on ? '1' : '0'); } catch (e) { /* no storage */ }
  if (on && window._actx) { try { window._actx.resume(); } catch (e) { /* ignore */ } }
}
export function soundOn(): boolean { return !MUTED; }

/** Lazily create/reuse a single shared AudioContext.
 *  When muted, throws — each SFX is try/catch-wrapped, so it just stays silent. */
function ac(): AudioContext {
  if (MUTED) throw 0;
  const Ctx = window.AudioContext || (window.webkitAudioContext as typeof AudioContext);
  const c = (window._actx ??= new Ctx());
  // iOS/WebKit starts audio contexts suspended until resumed from a tap; resume on every use
  if (c.state === 'suspended') { try { c.resume(); } catch (e) { /* ignore */ } }
  return c;
}

export function shuffleSound() {
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const dur = 0.24;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.1) * (0.5 + 0.5 * Math.random());
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 750 + Math.random() * 650;
    const g = ctx.createGain();
    g.gain.value = 0.22;
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(t);
    /* a couple of tiny tile-on-tile ticks inside the slide */
    for (let k = 0; k < 2; k++) {
      const cd = 0.03;
      const cb = ctx.createBuffer(1, Math.floor(ctx.sampleRate * cd), ctx.sampleRate);
      const cdd = cb.getChannelData(0);
      for (let i = 0; i < cdd.length; i++) cdd[i] = (Math.random() * 2 - 1) * (1 - i / cdd.length);
      const cs = ctx.createBufferSource();
      cs.buffer = cb;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2200 + Math.random() * 1400;
      const cg = ctx.createGain();
      cg.gain.value = 0.09;
      cs.connect(bp); bp.connect(cg); cg.connect(ctx.destination);
      cs.start(t + 0.02 + Math.random() * 0.16);
    }
  } catch (e) {}
}

export function siren() {
  /* CAPICÚ siren: sweeping wail, three passes */
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'square';
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.22, t);
    g.gain.setValueAtTime(0.22, t + 1.35);
    g.gain.exponentialRampToValueAtTime(0.001, t + 1.65);
    o.frequency.setValueAtTime(620, t);
    for (let i = 0; i < 3; i++) {
      o.frequency.linearRampToValueAtTime(1280, t + i * 0.45 + 0.22);
      o.frequency.linearRampToValueAtTime(620, t + i * 0.45 + 0.45);
    }
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + 1.7);
  } catch (e) {}
}

export function explosion() {
  /* CHUCHAZO explosion: big boom with falling rumble + sub thump */
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const dur = 1.2;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.7);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(3400, t);
    lp.frequency.exponentialRampToValueAtTime(110, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(t);
    /* sub-bass thump underneath */
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(65, t);
    o.frequency.exponentialRampToValueAtTime(28, t + 0.5);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.7, t);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    o.connect(g2); g2.connect(ctx.destination);
    o.start(t); o.stop(t + 0.6);
  } catch (e) {}
}

export function celebration() {
  /* VICTORY at 500: rising fanfare + final chord + crowd cheer + confetti pops */
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const NOTES = [523.25, 659.25, 783.99, 1046.5];
    NOTES.forEach((f, i) => {
      const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.value = f;
      const g = ctx.createGain();
      const st = t + i * 0.16;
      g.gain.setValueAtTime(0.0001, st);
      g.gain.exponentialRampToValueAtTime(0.34, st + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, st + 0.5);
      o.connect(g); g.connect(ctx.destination);
      o.start(st); o.stop(st + 0.55);
    });
    const ct = t + 0.75; /* held final chord */
    for (const f of NOTES) {
      const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, ct);
      g.gain.exponentialRampToValueAtTime(0.2, ct + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, ct + 1.6);
      o.connect(g); g.connect(ctx.destination);
      o.start(ct); o.stop(ct + 1.7);
    }
    /* crowd cheer swell */
    const dur = 2.6;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.sin(Math.PI * i / d.length) * 0.8;
    }
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 1500; bp.Q.value = 0.5;
    const g = ctx.createGain(); g.gain.value = 0.25;
    src.connect(bp); bp.connect(g); g.connect(ctx.destination);
    src.start(t + 0.1);
    /* confetti pops */
    for (let k = 0; k < 6; k++) {
      const pd = 0.025;
      const pb = ctx.createBuffer(1, Math.floor(ctx.sampleRate * pd), ctx.sampleRate);
      const pdd = pb.getChannelData(0);
      for (let i = 0; i < pdd.length; i++) pdd[i] = (Math.random() * 2 - 1) * (1 - i / pdd.length);
      const ps = ctx.createBufferSource(); ps.buffer = pb;
      const pf = ctx.createBiquadFilter();
      pf.type = 'bandpass'; pf.frequency.value = 2800 + Math.random() * 1600;
      const pg = ctx.createGain(); pg.gain.value = 0.14;
      ps.connect(pf); pf.connect(pg); pg.connect(ctx.destination);
      ps.start(t + 0.3 + Math.random() * 1.6);
    }
  } catch (e) {}
}

export function womp() {
  /* sad trombone for the losing side */
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    [[233, 0], [207, 0.4], [185, 0.8], [147, 1.2]].forEach(([f, off]) => {
      const o = ctx.createOscillator(); o.type = 'sawtooth';
      const st = t + off;
      o.frequency.setValueAtTime(f, st);
      o.frequency.linearRampToValueAtTime(f * 0.94, st + (off >= 1.2 ? 0.8 : 0.38));
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, st);
      g.gain.exponentialRampToValueAtTime(0.2, st + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, st + (off >= 1.2 ? 0.9 : 0.42));
      o.connect(g); g.connect(ctx.destination);
      o.start(st); o.stop(st + 1);
    });
  } catch (e) {}
}

export function buzzer() {
  /* game-show buzzer for ¡NO VOY! */
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const o1 = ctx.createOscillator(); o1.type = 'sawtooth'; o1.frequency.value = 92;
    const o2 = ctx.createOscillator(); o2.type = 'square'; o2.frequency.value = 123;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.45, t);
    g.gain.setValueAtTime(0.45, t + 0.3);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
    o1.connect(g); o2.connect(g); g.connect(ctx.destination);
    o1.start(t); o2.start(t);
    o1.stop(t + 0.5); o2.stop(t + 0.5);
  } catch (e) {}
}

export function clack() {
  /* real domino-on-table strike: hard ivory CLICK + wooden table THUMP */
  try {
    const ctx = ac();
    const t = ctx.currentTime;

    /* 1) the ivory click: sharp filtered noise burst */
    const dur = 0.07;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2.4);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2400 + Math.random() * 1100; /* each ficha rings a little different */
    bp.Q.value = 1.1;
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.85, t);
    src.connect(bp); bp.connect(g1); g1.connect(ctx.destination);

    /* 2) the table thump: low knock decaying fast */
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(155 + Math.random() * 45, t);
    o.frequency.exponentialRampToValueAtTime(65, t + 0.09);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.5, t);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    o.connect(g2); g2.connect(ctx.destination);

    src.start(t);
    o.start(t); o.stop(t + 0.13);
  } catch (e) {}
}

/** Soft two-note "it's your turn" chime — gentle, non-annoying, iOS-safe. */
export function turnChime() {
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const notes = [660, 880]; // a pleasant rising two-note ping
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      const g = ctx.createGain();
      const start = t + i * 0.11;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      o.connect(g); g.connect(ctx.destination);
      o.start(start); o.stop(start + 0.3);
    });
  } catch (e) {}
}

export function megaBoom() {
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(58, t);
    o.frequency.exponentialRampToValueAtTime(24, t + 0.35);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.85, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + 0.45);
  } catch (e) {}
}

export function airhorn() {
  try {
    const ctx = ac();
    const t = ctx.currentTime;
    [415, 311, 208].forEach(f => {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(f, t);
      o.frequency.linearRampToValueAtTime(f * 1.06, t + 0.12);
      o.frequency.linearRampToValueAtTime(f, t + 0.55);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.16, t + 0.03);
      g.gain.setValueAtTime(0.16, t + 0.5);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.68);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + 0.7);
    });
  } catch (e) {}
}
